import { Ban, CircleCheck, Sparkles, Trophy, Upload, User } from 'lucide-react';
import React from 'react';
import { WrapRequest } from 'wrap-request';
import { SortingRule, TableComponent } from '@components/table';
import { Badge } from '@components/ui/badge';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { I18n } from '../../../core/i18n';
import { injectTSDI } from '../../../core/tsdi';
import { getCdnImage } from '../../../components/utils/cdn-image';
import { PageUserBadgeV2AdminDto, UserBadgeV2AdminDto } from './store';

interface UserBadgesTableProps {
    userBadges: WrapRequest<PageUserBadgeV2AdminDto>;
    sorting?: SortingRule | SortingRule[];
    onSortingChange: (sorting?: SortingRule) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onRemoveBadge: (badge: UserBadgeV2AdminDto) => void;
    onDeleteBadge: (badge: UserBadgeV2AdminDto) => void;
}

const getLocalizedName = (name: Record<string, string> | undefined): string => {
    if (!name) return 'Unnamed';
    return name['en'] || name[Object.keys(name)[0]] || 'Unnamed';
};

const getSourceIcon = (source: string) => {
    switch (source) {
        case 'MANUAL':
            return <User />;
        case 'AUTO':
            return <Sparkles />;
        case 'IMPORT':
            return <Upload />;
        default:
            return null;
    }
};

const getSourceLabel = (source: string): string => {
    switch (source) {
        case 'MANUAL':
            return 'Manual';
        case 'AUTO':
            return 'Auto';
        case 'IMPORT':
            return 'Import';
        default:
            return source
                .split(/[_\s]+/)
                .filter(Boolean)
                .map(
                    (word) =>
                        word[0].toUpperCase() + word.slice(1).toLowerCase()
                )
                .join(' ');
    }
};

const getLevelColor = (levelIndex: number): string => {
    if (levelIndex <= 1) return '#CD7F32';
    if (levelIndex <= 2) return '#C0C0C0';
    if (levelIndex <= 3) return '#FFD700';
    if (levelIndex <= 5) return '#E5E4E2';
    return '#B9F2FF';
};

export const UserBadgesTable: React.FC<UserBadgesTableProps> = ({
    userBadges,
    sorting,
    onSortingChange,
    onPageChange,
    onPageSizeChange,
    onRemoveBadge,
    onDeleteBadge
}) => {
    const { formatDateTime } = injectTSDI(I18n);

    const getBadgeImageUrl = (
        badge: UserBadgeV2AdminDto
    ): string | undefined => {
        if (badge.image?.bucket && badge.image?.key) {
            return getCdnImage(badge.image.bucket, badge.image.key, {
                resize: { width: 48, height: 48 }
            });
        }
        return undefined;
    };

    return (
        <TableComponent<UserBadgeV2AdminDto[]>
            items={userBadges}
            tableLayout="auto"
            emptyText="No badges found"
            columns={[
                {
                    Header: 'Badge',
                    id: 'badgeName',
                    accessor: (badge) => {
                        const imageUrl = getBadgeImageUrl(badge);
                        const levelColor = getLevelColor(badge.badgeLevelIndex);
                        return (
                            <div className="flex items-center gap-3">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted"
                                            style={{
                                                border: `2px solid ${levelColor}`,
                                                boxShadow: `0 0 8px ${levelColor}40`
                                            }}
                                        >
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt=""
                                                    className="size-full object-cover"
                                                />
                                            ) : (
                                                <Trophy
                                                    className="size-5"
                                                    style={{
                                                        color: levelColor
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Level {badge.badgeLevelIndex}
                                    </TooltipContent>
                                </Tooltip>
                                <div>
                                    <p className="text-sm font-medium">
                                        {getLocalizedName(badge.badgeLevelName)}
                                    </p>
                                    <span className="text-xs text-muted-foreground">
                                        {getLocalizedName(badge.badgeTypeName)}
                                    </span>
                                </div>
                            </div>
                        );
                    },
                    disableSortBy: true,
                    style: { minWidth: 220 }
                },
                {
                    Header: 'Category',
                    id: 'category',
                    accessor: (badge) => (
                        <Badge variant="outline">
                            {getLocalizedName(badge.badgeCategoryName)}
                        </Badge>
                    ),
                    disableSortBy: true,
                    style: { width: 140 }
                },
                {
                    Header: 'Level',
                    id: 'level',
                    accessor: (badge) => {
                        const levelColor = getLevelColor(badge.badgeLevelIndex);
                        return (
                            <div className="flex items-center gap-1.5">
                                <span
                                    className="size-2 rounded-full"
                                    style={{
                                        backgroundColor: levelColor,
                                        boxShadow: `0 0 4px ${levelColor}`
                                    }}
                                />
                                <span className="text-sm font-medium">
                                    {badge.badgeLevelIndex}
                                </span>
                            </div>
                        );
                    },
                    disableSortBy: true,
                    style: { width: 70 }
                },
                {
                    Header: 'Earned',
                    id: 'earnedAt',
                    accessor: (badge) => (
                        <span className="text-sm text-muted-foreground">
                            {formatDateTime(badge.earnedAt)}
                        </span>
                    ),
                    style: { width: 140 }
                },
                {
                    Header: 'Source',
                    id: 'source',
                    accessor: (badge) => (
                        <Badge variant="outline">
                            {getSourceIcon(badge.source)}
                            {getSourceLabel(badge.source)}
                        </Badge>
                    ),
                    disableSortBy: true,
                    style: { width: 100 }
                },
                {
                    Header: 'Status',
                    id: 'status',
                    accessor: (badge) =>
                        badge.status === 'EARNED' ? (
                            <Badge variant="success">
                                <CircleCheck />
                                Earned
                            </Badge>
                        ) : (
                            <Badge variant="destructive">
                                <Ban />
                                Revoked
                            </Badge>
                        ),
                    disableSortBy: true,
                    style: { width: 100 }
                }
            ]}
            sorting={Array.isArray(sorting) ? sorting[0] : sorting}
            onSortingChange={onSortingChange}
            onChangePage={onPageChange}
            onPageSizeChange={onPageSizeChange}
            contextMenu={[
                {
                    label: 'Revoke Badge',
                    action: (badge) => onRemoveBadge(badge),
                    hidden: (badge) => badge.status === 'REVOKED'
                },
                {
                    label: 'Delete Permanently',
                    action: (badge) => onDeleteBadge(badge)
                }
            ]}
        />
    );
};
